/**
 * Single source of truth for the locales the microsite ships
 * translations for. Each entry describes:
 *   - `code`        BCP-47 tag used by react-intl-next (`pt-BR`, not `pt_BR`)
 *   - `nativeName`  How the locale labels itself in the dropdown
 *   - `loadMessages` Lazy import of the Traduki bundle re-exported by
 *                    this package's `./i18n/<code>` subpath
 *
 * The keys of the dynamic-import paths use the underscore form because
 * that's how Traduki names the files (see ./src/i18n/*.ts).
 *
 * IMPORTANT: When a new locale is added under `src/i18n/`, append it
 * here. Nothing else needs to change.
 */

export type LocaleCode =
	| 'en'
	| 'en-GB'
	| 'en-ZZ'
	| 'cs'
	| 'da'
	| 'de'
	| 'es'
	| 'fi'
	| 'fr'
	| 'hr'
	| 'hu'
	| 'it'
	| 'ja'
	| 'ko'
	| 'nb'
	| 'nl'
	| 'pl'
	| 'pt-BR'
	| 'ru'
	| 'sl'
	| 'sr-RS'
	| 'sr-YR'
	| 'sv'
	| 'th'
	| 'tr'
	| 'uk'
	| 'vi'
	| 'zh'
	| 'zh-TW';

export type LocaleMessages = Record<string, string>;

export type LocaleDescriptor = {
	code: LocaleCode;
	nativeName: string;
	loadMessages: () => Promise<LocaleMessages>;
};

const importDefault = async <T>(p: Promise<{ default: T }>): Promise<T> => (await p).default;

export const SUPPORTED_LOCALES: readonly LocaleDescriptor[] = [
	{
		code: 'en',
		nativeName: 'English',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-en" */ '../../i18n/en')),
	},
	{
		code: 'en-GB',
		nativeName: 'English (UK)',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-en_GB" */ '../../i18n/en_GB')),
	},
	{
		// Pseudo-locale, useful for QA. Hidden from the dropdown via
		// `SELECTABLE_LOCALES` below but still loadable if persisted.
		code: 'en-ZZ',
		nativeName: 'English (pseudo)',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-en_ZZ" */ '../../i18n/en_ZZ')),
	},
	{
		code: 'cs',
		nativeName: 'Čeština',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-cs" */ '../../i18n/cs')),
	},
	{
		code: 'da',
		nativeName: 'Dansk',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-da" */ '../../i18n/da')),
	},
	{
		code: 'de',
		nativeName: 'Deutsch',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-de" */ '../../i18n/de')),
	},
	{
		code: 'es',
		nativeName: 'Español',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-es" */ '../../i18n/es')),
	},
	{
		code: 'fi',
		nativeName: 'Suomi',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-fi" */ '../../i18n/fi')),
	},
	{
		code: 'fr',
		nativeName: 'Français',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-fr" */ '../../i18n/fr')),
	},
	{
		code: 'hr',
		nativeName: 'Hrvatski',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-hr" */ '../../i18n/hr')),
	},
	{
		code: 'hu',
		nativeName: 'Magyar',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-hu" */ '../../i18n/hu')),
	},
	{
		code: 'it',
		nativeName: 'Italiano',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-it" */ '../../i18n/it')),
	},
	{
		code: 'ja',
		nativeName: '日本語',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-ja" */ '../../i18n/ja')),
	},
	{
		code: 'ko',
		nativeName: '한국어',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-ko" */ '../../i18n/ko')),
	},
	{
		code: 'nb',
		nativeName: 'Norsk bokmål',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-nb" */ '../../i18n/nb')),
	},
	{
		code: 'nl',
		nativeName: 'Nederlands',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-nl" */ '../../i18n/nl')),
	},
	{
		code: 'pl',
		nativeName: 'Polski',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-pl" */ '../../i18n/pl')),
	},
	{
		code: 'pt-BR',
		nativeName: 'Português (Brasil)',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-pt_BR" */ '../../i18n/pt_BR')),
	},
	{
		code: 'ru',
		nativeName: 'Русский',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-ru" */ '../../i18n/ru')),
	},
	{
		code: 'sl',
		nativeName: 'Slovenščina',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-sl" */ '../../i18n/sl')),
	},
	{
		code: 'sr-RS',
		nativeName: 'Српски',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-sr_RS" */ '../../i18n/sr_RS')),
	},
	{
		code: 'sr-YR',
		nativeName: 'Srpski',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-sr_YR" */ '../../i18n/sr_YR')),
	},
	{
		code: 'sv',
		nativeName: 'Svenska',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-sv" */ '../../i18n/sv')),
	},
	{
		code: 'th',
		nativeName: 'ไทย',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-th" */ '../../i18n/th')),
	},
	{
		code: 'tr',
		nativeName: 'Türkçe',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-tr" */ '../../i18n/tr')),
	},
	{
		code: 'uk',
		nativeName: 'Українська',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-uk" */ '../../i18n/uk')),
	},
	{
		code: 'vi',
		nativeName: 'Tiếng Việt',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-vi" */ '../../i18n/vi')),
	},
	{
		code: 'zh',
		nativeName: '简体中文',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-zh" */ '../../i18n/zh')),
	},
	{
		code: 'zh-TW',
		nativeName: '繁體中文',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-zh_TW" */ '../../i18n/zh_TW')),
	},
] as const;

/** Locales offered in the footer dropdown (excludes pseudo-locale). */
export const SELECTABLE_LOCALES: readonly LocaleDescriptor[] = SUPPORTED_LOCALES.filter(
	(l) => l.code !== 'en-ZZ',
);

export const DEFAULT_LOCALE: LocaleCode = 'en';

export const isSupportedLocale = (value: string | null | undefined): value is LocaleCode =>
	value !== null && value !== undefined && SUPPORTED_LOCALES.some((l) => l.code === value);

export const getDescriptor = (code: LocaleCode): LocaleDescriptor =>
	// Safe non-null assert: type guard ensures `code` is in the catalog.
	SUPPORTED_LOCALES.find((l) => l.code === code) as LocaleDescriptor;
